/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */

import { Component, Input, OnInit } from '@angular/core';
import { IconShapeCollection } from '@cds/core/icon/interfaces/icon.interfaces';

import { getIconVariants, IconVariant } from './icon-variants.pipe';
import { IconGroupsFormOptions } from '../icon-groups/icon-groups.component';

@Component({
  selector: 'app-icon-details',
  templateUrl: 'icon-details.component.html',
  styleUrls: ['./icon-details.component.scss'],
})
export class IconDetailsComponent implements OnInit {
  @Input() iconName: string;
  @Input() iconDetails: IconShapeCollection;
  @Input() formOptions: IconGroupsFormOptions;

  selectedIconVariant: IconVariant;

  ngOnInit() {
    this.selectedIconVariant = getInitiallySelectedIconVariant(this.iconDetails, this.formOptions);
  }

  downloadSelectedIcon() {
    const filename = getIconFileName(this.iconName, this.selectedIconVariant);
    const svg = getSelectedIconVariantSvg();

    downloadTextFile(`${filename}.svg`, svg);
  }
}

function getInitiallySelectedIconVariant(iconDetails: IconShapeCollection, formOptions: IconGroupsFormOptions) {
  const iconVariants = getIconVariants(iconDetails);

  return findExactMatch() || findSolidMatch() || iconVariants[0];

  function findExactMatch() {
    return iconVariants.find(
      iconVariant => iconVariant.solid === formOptions.solid && iconVariant.badge === formOptions.badge
    );
  }

  function findSolidMatch() {
    return iconVariants.find(iconVariant => iconVariant.solid === formOptions.solid);
  }
}

function getIconFileName(iconName: string, iconVariant: IconVariant) {
  let filename = iconName;

  filename += iconVariant?.solid ? '-solid' : '-outline';

  if (iconVariant?.badge?.includes('triangle')) {
    filename += '-alerted';
  } else if (iconVariant?.badge) {
    filename += '-badged';
  }

  return filename;
}

function getSelectedIconVariantSvg() {
  const iconElement = document.querySelector('button.icon-detail-card__variant--selected cds-icon');

  const divElement = document.createElement('div');
  divElement.innerHTML = iconElement.shadowRoot.innerHTML;

  const svgElement = divElement.querySelector('svg');
  svgElement.setAttribute('width', '36');
  svgElement.setAttribute('height', '36');
  svgElement.removeAttribute('aria-hidden');

  return svgElement.outerHTML;
}

function downloadTextFile(filename: string, contents: string) {
  const linkElement = document.createElement('a');
  linkElement.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(contents)}`);
  linkElement.setAttribute('download', filename);
  linkElement.style.display = 'none';

  document.body.appendChild(linkElement);
  linkElement.click();
  document.body.removeChild(linkElement);
}
